import React, { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { space } from 'styled-system';

import { KIOSK_PRO_MEDIA, kioskTouchHints } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import useCart from 'shared/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import { ConsumerInfo } from 'src/components/kiosk-pro/kiosk-pro-layout/consumer-info';
import { KioskLoginModal } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-modal';
import { getKioskSessionGeneration } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-session-teardown';
import { useKioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout/use-kiosk-pro-layout';
import { KioskProNavigation } from 'src/components/kiosk-pro/kiosk-pro-layout/navigation';
import { EndSessionModal } from 'src/components/kiosk-pro/kiosk-pro-layout/end-session-modal';
import { KioskProTouchDiagnostics } from 'src/components/kiosk-pro/touch-diagnostics';
import type { Dispensaries } from 'src/gql/graphql';

type KioskProLayoutProps = {
  dispensary: Dispensaries;
  children: React.ReactNode;
};

export const KioskProLayout = ({ dispensary, children }: KioskProLayoutProps): JSX.Element => {
  const {
    isConsumerPhoneLoginPage,
    startNewSession,
    goToCart,
    kioskProModalLogin,
    createPublicContextSessionLoading,
    isKioskProLoginModalOpen,
    publicLoginSessionId,
    setIsKioskProLoginModalOpen,
    consumerPhoneLoginUrl,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    confirmedStartNewSession,
  } = useKioskProLayout({ dispensary });

  const Cart = useCart();
  const UI = useUI();
  // Lets the concierge bridge start a Kiosk Pro login through the same action as the
  // "Log in" button. The ref keeps the registered callback on the latest closure.
  const kioskProModalLoginRef = useRef(kioskProModalLogin);
  kioskProModalLoginRef.current = kioskProModalLogin;
  // One login at a time per session: repeated bridge requests while one is in flight share it, so
  // they do not create several public sessions. A login started before a session end is not shared
  // with a request made after it (that login is dropped), so the entry records its generation.
  const inFlightLoginRef = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  useEffect(() => {
    UI.setKioskProLoginRequestHandler(() => {
      const generation = getKioskSessionGeneration();
      if (inFlightLoginRef.current?.generation === generation) {
        return inFlightLoginRef.current.promise;
      }
      const entry: { generation: number; promise: Promise<void> } = {
        generation,
        promise: kioskProModalLoginRef.current().finally(() => {
          if (inFlightLoginRef.current === entry) {
            inFlightLoginRef.current = null;
          }
        }),
      };
      inFlightLoginRef.current = entry;
      return entry.promise;
    });
    return () => UI.setKioskProLoginRequestHandler(undefined);
  }, [UI]);
  // Deprecated, but intentionally kept: this force-re-renders the whole layout on a
  // Cart.subtotal change, and that re-render is one of the incidental triggers through which
  // untracked UI reads below (e.g. isKioskProLoginModalOpen) reach the DOM. Removing it is a
  // behaviour change -- see "Noticed, out of scope" in the PR description.
  const orderSubtotal = useObserver(() => Cart.subtotal);
  const disableFasterCheckout = dispensary.orderTypesConfigV2?.kiosk?.disableFasterCheckout ?? false;

  return (
    <TwoColumn padding='0'>
      <TwoColumnSidebar>
        <KioskProNavigation />
      </TwoColumnSidebar>

      <TwoColumnContent>{children}</TwoColumnContent>

      {!isConsumerPhoneLoginPage && (
        <ConsumerInfo
          createPublicContextSessionLoading={createPublicContextSessionLoading}
          goToCart={goToCart}
          kioskProModalLogin={kioskProModalLogin}
          orderSubTotal={orderSubtotal}
          startNewSession={startNewSession}
        />
      )}

      {!disableFasterCheckout && isKioskProLoginModalOpen && !!publicLoginSessionId && (
        <KioskLoginModal
          isOpen={isKioskProLoginModalOpen}
          handleClose={() => setIsKioskProLoginModalOpen(false)}
          consumerPhoneLoginUrl={consumerPhoneLoginUrl}
        />
      )}

      <EndSessionModal
        startNewSession={() => void confirmedStartNewSession()}
        isOpen={isEndSessionModalOpen}
        handleCloseModal={() => setIsEndSessionModalOpen(false)}
      />

      <KioskProTouchDiagnostics dispensary={dispensary} />
    </TwoColumn>
  );
};

const TwoColumn = styled.div`
  display: flex;
  padding: 0;

  ${space}

  a, button {
    ${kioskTouchHints}
  }

  @media (orientation: portrait) {
    flex-direction: column;
  }
`;

const TwoColumnSidebar = styled.div`
  width: 400px;
  background-color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};

  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    width: 320px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    width: 280px;
  }

  @media (orientation: portrait) {
    width: 100%;
  }
`;

export const TwoColumnContent = styled.div`
  padding: 10px;
  flex: 1 0 0%;
  min-width: 0;
  min-height: 100vh;
  --kiosk-pro-content-width: 100%;

  .two-column-content {
    padding: 26px 0 0;
  }

  .embla__viewport {
    width: calc(100% + 52px);
    margin-left: -26px;

    @media (min-width: 770px) {
      width: 100%;
      margin-left: 0;
    }
  }

  @media (orientation: portrait) {
    padding-bottom: 300px;
    padding-top: 250px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding-bottom: 280px;
    padding-top: 210px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    padding-bottom: 200px;
    padding-top: 180px;
  }
`;
